import { useState, useEffect } from "react";
import type { FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { OrderQuantitySelector } from "./OrderQuantitySelector";
import type { PaymentRequest } from "@stripe/stripe-js";
import {
  useStripe,
  useElements,
  CardElement,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";

interface Product {
  _id: string;
  name?: string;
  price: number;
}

interface CheckoutPaymentStepProps {
  products: Product[];
  quantities: Record<string, number>;
  onQuantityChange: (productId: string, quantity: number) => void;
  cartItems: Array<{ product: Product; quantity: number }>;
  total: number;
  name: string;
  email: string;
  onPaymentMethod: (paymentMethodId: string, tipAmount?: number) => Promise<void>;
  isProcessing: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  setIsProcessing: (isProcessing: boolean) => void;
  checkoutSettings: {
    tipping?: {
      enabled: boolean;
      amount1?: number;
      amount2?: number;
      amount3?: number;
    };
  } | null;
}

const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#424770",
      "::placeholder": {
        color: "#aab7c4",
      },
    },
    invalid: {
      color: "#9e2146",
    },
  },
};

export function CheckoutPaymentStep({
  products,
  quantities,
  onQuantityChange,
  cartItems,
  total,
  name,
  email,
  onPaymentMethod,
  isProcessing,
  error,
  setError,
  setIsProcessing,
  checkoutSettings,
}: CheckoutPaymentStepProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(null);
  const [prVersion, setPrVersion] = useState(0);
  const [showTip, setShowTip] = useState(false);
  const [selectedTipAmount, setSelectedTipAmount] = useState<number | null>(null);
  const [customTipAmount, setCustomTipAmount] = useState<string>("");

  const getTipAmount = () => {
    if (!showTip) return 0;
    if (selectedTipAmount !== null) return selectedTipAmount;
    const customAmount = parseFloat(customTipAmount);
    return isNaN(customAmount) || customAmount < 0 ? 0 : customAmount;
  };

  const finalTotal = total + getTipAmount();

  useEffect(() => {
    if (stripe && total > 0) {
      const tipAmount = getTipAmount();
      const pr = stripe.paymentRequest({
        country: "US",
        currency: "usd",
        total: {
          label: "Order Total",
          amount: Math.round((total + tipAmount) * 100),
        },
        requestPayerEmail: true,
        requestPayerName: true,
      });

      pr.canMakePayment().then((result) => {
        if (result) {
          setPaymentRequest(pr);
          setPrVersion((prev) => prev + 1);
        } else {
          setPaymentRequest(null);
        }
      });

      pr.on("paymentmethod", async (ev: any) => {
        setIsProcessing(true);
        setError(null);
        try {
          await onPaymentMethod(ev.paymentMethod.id, tipAmount > 0 ? tipAmount : undefined);
          ev.complete("success");
        } catch (err) {
          ev.complete("fail");
          setError(err instanceof Error ? err.message : "An error occurred");
          setIsProcessing(false);
        }
      });
    }
  }, [stripe, total, name, email, onPaymentMethod, setIsProcessing, setError, showTip, selectedTipAmount, customTipAmount]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      setError("Card element not found");
      setIsProcessing(false);
      return;
    }

    try {
      const result = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: { name, email },
      });

      if (result.error) {
        setError(result.error.message || "An error occurred");
        setIsProcessing(false);
        return;
      }

      const tipAmount = getTipAmount();
      await onPaymentMethod(result.paymentMethod.id, tipAmount > 0 ? tipAmount : undefined);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4 flex-1">
      <form onSubmit={handleSubmit} className="space-y-4">
        <OrderQuantitySelector
          products={products}
          quantities={quantities}
          onQuantityChange={onQuantityChange}
          total={total}
          tipAmount={showTip ? getTipAmount() : 0}
        />

        {checkoutSettings?.tipping?.enabled && (
          <div className="py-4 space-y-4 border-t border-gray-300">
            <h3 className="text-lg font-semibold text-black">Add tip</h3>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="show-tip"
                checked={showTip}
                onCheckedChange={(checked) => {
                  setShowTip(checked === true);
                  if (checked) {
                    if (checkoutSettings.tipping?.amount1 !== undefined) {
                      setSelectedTipAmount(checkoutSettings.tipping.amount1);
                    }
                    setCustomTipAmount("");
                  } else {
                    setSelectedTipAmount(null);
                    setCustomTipAmount("");
                  }
                }}
              />
              <Label htmlFor="show-tip" className="text-black cursor-pointer">
                Show your support with a tip
              </Label>
            </div>
            {showTip && (
              <div className="space-y-3 pl-6">
                <div className="flex gap-2 flex-wrap">
                  {checkoutSettings.tipping?.amount1 !== undefined && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedTipAmount(checkoutSettings.tipping!.amount1!);
                        setCustomTipAmount("");
                      }}
                      className={selectedTipAmount === checkoutSettings.tipping.amount1 ? "bg-gray-500 text-white hover:bg-gray-600" : "text-black"}
                    >
                      ${checkoutSettings.tipping.amount1.toFixed(2)}
                    </Button>
                  )}
                  {checkoutSettings.tipping?.amount2 !== undefined && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedTipAmount(checkoutSettings.tipping!.amount2!);
                        setCustomTipAmount("");
                      }}
                      className={selectedTipAmount === checkoutSettings.tipping.amount2 ? "bg-gray-500 text-white hover:bg-gray-600" : "text-black"}
                    >
                      ${checkoutSettings.tipping.amount2.toFixed(2)}
                    </Button>
                  )}
                  {checkoutSettings.tipping?.amount3 !== undefined && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setSelectedTipAmount(checkoutSettings.tipping!.amount3!);
                        setCustomTipAmount("");
                      }}
                      className={selectedTipAmount === checkoutSettings.tipping.amount3 ? "bg-gray-500 text-white hover:bg-gray-600" : "text-black"}
                    >
                      ${checkoutSettings.tipping.amount3.toFixed(2)}
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="custom-tip" className="text-black">
                    Custom tip
                  </Label>
                  <Input
                    id="custom-tip"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={customTipAmount}
                    onChange={(e) => {
                      setCustomTipAmount(e.target.value);
                      setSelectedTipAmount(null);
                    }}
                    className="text-black"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        <div className="py-4 space-y-4">
          {paymentRequest && (
            <>
              <div className="relative bg-white rounded" style={{ height: "48px" }}>
                <PaymentRequestButtonElement
                  key={prVersion}
                  options={{
                    paymentRequest,
                    style: {
                      paymentRequestButton: {
                        type: "default",
                        theme: "light",
                        height: "48px",
                      },
                    },
                  }}
                />
              </div>
              <div className="w-full border-b border-gray-500 my-6 relative">
                <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 bg-white text-gray-500 px-4 text-sm">
                  Or pay with card
                </div>
              </div>
            </>
          )}
          <div className="space-y-2">
            <div className="p-3 border border-gray-300 rounded-md">
              <CardElement options={cardElementOptions} />
            </div>
          </div>
          {error && <p className="text-sm text-red-500 mt-2">{error}</p>}
        </div>

        <button
          type="submit"
          className="w-full bg-black text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={!stripe || isProcessing || total === 0}
        >
          {isProcessing ? "Processing..." : "Place Order"}
        </button>
      </form>
    </div>
  );
}
